import { FilterModel, IDateComp, IDateParams } from '@ag-grid-community/core';

import {
  clearColumnRelativeDate,
  clearRelativeDateButton,
  noteRelativeDateButton,
  setColumnRelativeDate,
} from './relativeDateFilters';
import { getLocalToday, startOfLocalDay } from './utils';

type RelativeDayOption = {
  label: string;
  offset: -1 | 0 | 1;
};

const RELATIVE_DAY_OPTIONS: RelativeDayOption[] = [
  { label: 'Today', offset: 0 },
  { label: 'Tomorrow', offset: 1 },
  { label: 'Yesterday', offset: -1 },
];

const toDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

type FilterParamsWithCol = {
  relativeDateColId?: string;
};

export class RelativeDateInput implements IDateComp {
  private eGui!: HTMLDivElement;
  private dateInput!: HTMLInputElement;
  private params!: IDateParams;
  private skipNextInputChange = false;

  private getColId(): string | undefined {
    const filterParams = this.params.filterParams as
      | FilterParamsWithCol
      | undefined;
    return filterParams?.relativeDateColId;
  }

  private onDateInputChange = () => {
    if (this.skipNextInputChange) {
      this.skipNextInputChange = false;
      return;
    }
    clearRelativeDateButton();
    this.params.onDateChanged();
    this.syncRelativeParams(null);
  };

  init(params: IDateParams) {
    this.params = params;

    this.eGui = document.createElement('div');
    this.eGui.style.display = 'flex';
    this.eGui.style.flexDirection = 'column';
    this.eGui.style.gap = '4px';
    this.eGui.style.width = '100%';

    this.dateInput = document.createElement('input');
    this.dateInput.type = 'date';
    this.dateInput.className = 'ag-input-field-input ag-text-field-input';
    this.dateInput.style.width = '100%';
    this.dateInput.addEventListener('input', this.onDateInputChange);
    this.dateInput.addEventListener('change', this.onDateInputChange);

    const shortcuts = document.createElement('div');
    shortcuts.style.display = 'flex';
    shortcuts.style.flexDirection = 'column';
    shortcuts.style.gap = '4px';
    shortcuts.style.width = '100%';

    const caption = document.createElement('div');
    caption.textContent =
      'Rolling filters — keep matching Today / Tomorrow / Yesterday going forward';
    caption.style.fontSize = '11px';
    caption.style.lineHeight = '1.3';
    caption.style.opacity = '0.75';

    const buttons = document.createElement('div');
    buttons.style.display = 'flex';
    buttons.style.gap = '8px';
    buttons.style.flexWrap = 'wrap';

    RELATIVE_DAY_OPTIONS.forEach(({ label, offset }) => {
      const shortcut = document.createElement('button');
      shortcut.type = 'button';
      shortcut.textContent = label;
      shortcut.className = 'ag-standard-button';
      shortcut.style.padding = '0 4px';
      shortcut.style.minHeight = '20px';
      shortcut.style.fontSize = '11px';
      shortcut.style.lineHeight = '1.2';
      shortcut.style.cursor = 'pointer';
      shortcut.addEventListener('mousedown', (event) => {
        event.preventDefault();
      });
      shortcut.addEventListener('click', () => {
        const target = startOfLocalDay(getLocalToday());
        target.setDate(target.getDate() + offset);
        const key = toDateKey(target);
        const colId = this.getColId();

        // Persist before AG Grid churn so cache save still sees the click.
        noteRelativeDateButton(offset, key, colId);

        this.skipNextInputChange = true;
        this.setDate(target);
        this.params.onDateChanged();
        this.syncRelativeParams(offset);
      });
      buttons.appendChild(shortcut);
    });

    shortcuts.appendChild(caption);
    shortcuts.appendChild(buttons);

    this.eGui.appendChild(this.dateInput);
    this.eGui.appendChild(shortcuts);
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  getDate(): Date | null {
    if (!this.dateInput.value) {
      return null;
    }

    const [year, month, day] = this.dateInput.value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  setDate(date: Date | null): void {
    this.dateInput.value = date ? toDateKey(date) : '';
  }

  setDisabled(disabled: boolean): void {
    this.dateInput.disabled = disabled;
  }

  setInputPlaceholder(placeholder: string): void {
    this.dateInput.placeholder = placeholder;
  }

  setInputAriaLabel(label: string): void {
    this.dateInput.setAttribute('aria-label', label);
  }

  destroy(): void {
    this.dateInput.removeEventListener('input', this.onDateInputChange);
    this.dateInput.removeEventListener('change', this.onDateInputChange);
  }

  private syncRelativeParams(offset: number | null) {
    const colId = this.getColId();
    if (!colId) return;

    const apply = () => {
      const model = {
        ...this.params.api.getFilterModel(),
      } as FilterModel;

      if (offset === null) {
        clearColumnRelativeDate(colId);
        return;
      }

      const filter = model[colId] as Record<string, unknown> | undefined;
      if (filter && filter.filterType === 'date' && filter.type === 'equals') {
        setColumnRelativeDate(colId, { delta: offset });
      }
    };

    apply();
    requestAnimationFrame(apply);
  }
}
